import {
  Amount,
  Contact,
  GenericBindResponse,
  GenericQuoteSuccessResponse,
  TrackingCode,
} from '@sky-distribution/adapter-core-pc-rest-interfaces';
import { BuyResponse } from '@sky-distribution/backend-gp-onboarding-interfaces';
import { Consent } from '@sky-distribution/digital-gp-submission';
import {
  apiBindWithOTP,
  apiBuy,
  apiCreate,
  apiQuote,
  apiQuoteRenewal,
  apiRate,
  apiRetrieveSales,
  apiUpdate,
} from '../api/api';
import { getSubmissionWithMetaData } from '../util';
import { getSubmissionConsents } from '../util/consents';
import {
  CreateResponse,
  DetailsResponse,
  RateArgs,
  RateResponse,
  Submission,
  SubmissionIds,
  UpdateResponse,
} from './Policy.service.type';

class PolicyService {
  async rate(rateData: RateArgs, productId: string): Promise<RateResponse> {
    return apiRate(rateData, productId);
  }

  create(
    arg: Submission,
    productId: string,
    consentStatus: Record<string, boolean>,
    consentDictionary: Consent[],
    trackingCodes?: TrackingCode[],
    tenant?: string,
    context?: string,
  ): Promise<CreateResponse> {
    const submission = getSubmissionWithMetaData(arg, consentStatus, consentDictionary, trackingCodes);

    return apiCreate(submission, productId, tenant, context);
  }

  update(
    arg: Submission,
    ids: SubmissionIds,
    productId: string,
    consentStatus: Record<string, boolean>,
    consentDictionary: Consent[],
    trackingCodes?: TrackingCode[],
    context?: string,
  ): Promise<UpdateResponse> {
    const submission = getSubmissionWithMetaData(arg, consentStatus, consentDictionary, trackingCodes);

    return apiUpdate({ data: submission, ids }, productId, context);
  }

  quote(
    arg: Submission,
    ids: SubmissionIds,
    productId: string,
    consentStatus: Record<string, boolean>,
    consentDictionary: Consent[],
    trackingCodes?: TrackingCode[],
    context?: string,
    tenant?: string,
  ): Promise<UpdateResponse> {
    const submission = getSubmissionWithMetaData(arg, consentStatus, consentDictionary, trackingCodes);

    return apiQuote({ data: submission, ids }, productId, context, tenant);
  }

  quoteRenewal(
    ids: SubmissionIds,
    productId: string,
    consentStatus: Record<string, boolean>,
    consentDictionary: Consent[],
  ): Promise<GenericQuoteSuccessResponse> {
    const quotePayload = {
      ...ids,
      baseData: {
        ...getSubmissionConsents(consentStatus, consentDictionary),
      },
    };

    return apiQuoteRenewal(quotePayload, productId);
  }

  buy(
    arg: Submission,
    ids: SubmissionIds,
    productId: string,
    totalPrice?: Amount,
    returnUrl?: string,
    brand?: string,
  ): Promise<BuyResponse> {
    const { emailAddress1, firstName, lastName } = arg.baseData.accountHolder
      .person as Contact;

    const absoluteReturnUrl = window.location.origin && returnUrl
      ? `${window.location.origin}${returnUrl}`
      : undefined;

    return apiBuy(
      {
        amount: totalPrice?.amount as number,
        emailAddress: emailAddress1 as string,
        ids,
        firstName: firstName as string,
        lastName: lastName as string,
        description: '',
        returnUrl: absoluteReturnUrl,
        brand,
      },
      productId,
    );
  }

  bindWithOTP(
    arg: Submission,
    ids: SubmissionIds,
    productId: string,
    otpCode?: string,
    totalPrice?: Amount,
  ): Promise<GenericBindResponse> {
    return apiBindWithOTP(
      {
        phoneNumber: arg.baseData.accountHolder.person?.homeNumber as string,
        otpCode: otpCode as string,
        amount: totalPrice as Amount,
        ids,
      },
      productId,
    );
  }

  retrieveService(
    ids: SubmissionIds,
    productId: string,
  ): Promise<DetailsResponse> {
    return apiRetrieveSales({ ids }, productId);
  }
}

export const policyService = new PolicyService();
